import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useIntl } from 'react-intl';
import { Form, FormItem, Input, ResetButton, Select, SubmitButton, Switch } from 'formik-antd';
import { Button, Card, Col, message, Row, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';
import ReactJson from 'react-json-view';
import isEmpty from 'lodash/isEmpty';
import { InfoCircleOutlined } from '@ant-design/icons';
import { RiArrowLeftLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import urlPageTemplatesVisualizations from '../../../../urls/urlPageTemplatesVisualizations';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import { hasRights, PrivateComponent } from '../../../../components/HasRights/HasRights';
import useDebounce from '../../../../app/hooks/useDebounce';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import getTemplateOptions from '../../../../lib/getTemplateOptions';
import validateHtml from '../../utils/validateHtml';
import Spinner from '../../../../components/Spinner';
import HtmlValidationErrorsRender from '../../components/HtmlValidationErrorsRender';
import HTMLPreview from '../../components/HTMLPreview';
import { Permissions } from '../../../../const/permissions';
import { mkValidationSchema } from './FormCreateEditTemplatesVisualization.const';
import { NEWS_TEMPLATE_CONTENT_TYPE } from '../../../Content/forms/FormCreateDynamicNews/FormCreateDynamicNews.const';
import { PUSH_NOTIFICATIONS_CONTENT_TYPE } from '../../../Other/forms/FormCreatePushNotification/FormCreatePushNotification.const';
import { useGetTemplatesQuery, useGetTemplateVisualizationsKeysQuery } from '../../api/templatesApiSlice';



const { Title } = Typography;

const FormCreateEditTemplatesVisualization = ({
  isSubmitting,
  initialErrors,
  onSubmit,
  initialValues = {},
  isEdit = false,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [ currentTemplateId, setCurrentTemplateId ] = useState(null);
  const [ htmlErrors, setHtmlErrors ] = useState([]);
  const [ html, setHtml ] = useState('');
  const renderHtml = useDebounce(html, 500);

  const { data: templates = { data: [] }, isFetching: isTemplatesFetching } = useGetTemplatesQuery(
    { queryParams: `limit=0&search=content_type:${NEWS_TEMPLATE_CONTENT_TYPE},${PUSH_NOTIFICATIONS_CONTENT_TYPE}` },
    { skip: isEdit },
  );

  const {
    data: { data: templateData } = { data: {} },
    isFetching: isTemplateDataFetching,
  } = useGetTemplateVisualizationsKeysQuery(
    currentTemplateId,
    { skip: !(currentTemplateId && hasRights([ Permissions.CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.TEMPLATE_VISUALIZATIONS_KEYS ])) },
  );

  useEffect(() => {
    if (!isEmpty(initialValues) && isEdit) {
      setHtml(initialValues.html);
      setCurrentTemplateId(initialValues.template_id);
    }
  }, [ initialValues ]);

  useEffect(() => {
    const form = document.querySelector('form');

    if (form) {
      const textArea = form.querySelector('textarea[name="html"]');

      if (textArea && textArea.value) {
        const validationErrors = validateHtml(textArea.value.trim(), intl);

        setHtmlErrors(validationErrors);
      }
    }
  }, [ intl ]);


  const handleCopy = (value) => {
    navigator.clipboard.writeText(value.src).catch(() => {
      message.error('Failed to copy to clipboard.');
    });
  };

  return (
    <Formik
      initialErrors={initialErrors}
      initialValues={initialValues}
      enableReinitialize
      isSubmitting={isSubmitting}
      validationSchema={mkValidationSchema}
      validate={(values) => {
        const errors = {};

        if (values.html) {

          const validationErrors = validateHtml(values.html.trim(), intl);

          setHtmlErrors(validationErrors);
          if (validationErrors.length > 0) {
            errors.html = validationErrors;
          }
        }
        setHtml(values.html);
        return errors;
      }}
      onSubmit={onSubmit}
    >
      {({ isValid, dirty, setFieldValue, resetForm }) => {
        return (
          <Form layout="vertical" >
            <Spinner spinning={isTemplatesFetching || isSubmitting}>
              <Title className="hp-mb-24" level={4}>
                {intl.formatMessage({ id: 'templates-visualizations-breadcrumbs' })}
              </Title>
              <Row gutter={[ 16, 16 ]} justify="space-between">
                <Col xs={24} md={12} xl={6} xxl={8}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-title' })}
                        name='title'
                        required
                      >
                        <Input
                          name="title"
                          placeholder={intl.formatMessage({ id: 'templates-visualizations-form-title-placeholder' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-template' })}
                        name='template_id'
                        required
                      >
                        <Select
                          disabled={isEdit}
                          loading={isTemplatesFetching}
                          allowClear
                          showSearch
                          name="template_id"
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                          filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
                          options={!isEdit ? getTemplateOptions(templates.data) : [ { label: initialValues?.template_name, value: initialValues?.template_id } ]}
                          onChange={async (value) => {
                            setCurrentTemplateId(value);
                            await setFieldValue('template_id', value);
                          }}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-html' })}
                        name='html'
                        required
                        help={<HtmlValidationErrorsRender htmlErrors={htmlErrors} />}
                      >
                        <Input.TextArea
                          rows={10}
                          maxLength={65535}
                          showCount
                          name="html"
                          type="text"
                          placeholder={intl.formatMessage({ id: 'ui-general-specify-html' })}
                          onChange={(ev) => {
                            const { value } = ev.target;
                            const validationErrors = validateHtml(value.trim(), intl);

                            setHtmlErrors(validationErrors);
                          }}
                          onBlur={(ev) => {
                            const { value } = ev.target;
                            const validationErrors = validateHtml(value.trim(), intl);

                            setHtmlErrors(validationErrors);
                          }}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-description' })}
                        name='description'
                      >
                        <Input.TextArea
                          rows={3}
                          maxLength={255}
                          showCount
                          name="description"
                          type="text"
                          placeholder={intl.formatMessage({ id: 'ui-general-specify-description' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        name='is_default'
                        label={
                          <Row className='w-full' justify="space-between" align="middle">
                            <IntlMessages id="ui-general-default" />
                            <Tooltip placement="topRight" title={<IntlMessages id="templates-visualizations-form-is-default-tooltip" />}>
                              <InfoCircleOutlined className="hp-ml-8" />
                            </Tooltip>
                          </Row>
                        }
                      >
                        <Switch name="is_default" />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.TEMPLATE_VISUALIZATIONS_KEYS ]}>
                  <Col xs={24} md={12} xl={9} xxl={8}>
                    <Title
                      level={5}
                      className="hp-mb-8"
                      style={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.5715 }}
                    >
                      {intl.formatMessage({ id: 'templates-visualizations-form-config-title' })}
                    </Title>
                    <Card
                      className="hp-mb-12"
                      style={{ height: '650px', overflowY: 'scroll' }}
                      loading={isTemplateDataFetching}
                    >
                      <ReactJson
                        enableClipboard={handleCopy}
                        theme="summerfruit:inverted"
                        src={currentTemplateId && Array.isArray(templateData)
                          ? Object.fromEntries(templateData.map((item) => [ item.display_name, `{{${item.name}}}` ]))
                          : {}}
                      />
                    </Card>
                  </Col>
                </PrivateComponent>

                <Col xs={24} md={24} xl={9} xxl={8}>
                  <HTMLPreview html={renderHtml} hasErrors={htmlErrors.length > 0} />
                </Col>
              </Row>

              <Row gutter={[ 16, 16 ]} justify='end'>
                <Col>
                  <Button
                    icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
                    onClick={() => navigate(urlPageTemplatesVisualizations())}
                  >
                    <IntlMessages id='ui-general-back-to-list' />
                  </Button>
                </Col>

                <Col>
                  <ResetButton
                    disabled={false}
                    onClick={() => {
                      resetForm();
                      if (!isEdit) {
                        setHtml('');
                        setCurrentTemplateId(null);
                      } else {
                        setHtml(initialValues.html);
                      }
                    }}
                  >
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton
                    type="primary"
                    loading={isSubmitting}
                    disabled={!isValid || !dirty}
                  >
                    <IntlMessages id="ui-general-save" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormCreateEditTemplatesVisualization.propTypes = {
  initialErrors: PropTypes.object.isRequired,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool,
};

export default FormCreateEditTemplatesVisualization;
